import { useCallback } from "react";
import classNames from "classnames";
import Icon from "~/components/shared/Icon";
import { isFailed, State, Step } from "~/lib/pipeline";
import { StepItem } from "~/components/build/Show/routes/steps/list/-components/TreeNode/StepItem";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { CollapsibleState } from "~/components/build/Show/routes/steps/list/-components/TreeItem/CollapsibleState";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";

interface Props {
  id?: string;
  step: Step;
  collapsibleState?: CollapsibleState;
  forcedCollapsibleState?: CollapsibleState;
  depth?: number;
  isLoading?: boolean;
  isLoaded?: boolean;
}

export function CollapsibleStepItem({
  id,
  step,
  depth = 0,
  collapsibleState = CollapsibleState.Collapsed,
  forcedCollapsibleState,
  isLoading = false,
  isLoaded,
}: Props) {
  const params = useQueryParams();
  const setCollapsibleState = useBuildStore((state) => state.setCollapsibleState);
  const active = params.stepUuid === step.uuid;
  const toggleable = forcedCollapsibleState === undefined;
  const isExpanded =
    !toggleable ||
    collapsibleState === CollapsibleState.Expanded ||
    collapsibleState === CollapsibleState.Full;

  // Use the node ID (which may be namespaced, e.g. for state-scoped groups)
  // to store collapsible state, falling back to step.uuid.
  const nodeId = id ?? step.uuid;

  const toggle = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setCollapsibleState(
        nodeId,
        isExpanded ? CollapsibleState.Collapsed : CollapsibleState.Expanded,
      );
    },
    [setCollapsibleState, nodeId, isExpanded],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === " ") {
        event.preventDefault();
        setCollapsibleState(
          nodeId,
          isExpanded ? CollapsibleState.Collapsed : CollapsibleState.Expanded,
        );
      }
    },
    [setCollapsibleState, nodeId, isExpanded],
  );

  return (
    <StepItem
      step={step}
      active={active}
      depth={depth}
      isLoaded={isLoaded}
      onKeyDown={toggleable ? handleKeyDown : undefined}
      failed={isFailed(step) && !isExpanded}
      className={classNames({
        "animate-pulse": isLoading,
      })}
      arrow={
        toggleable ? (
          <button
            type="button"
            onClick={toggle}
            tabIndex={-1}
            data-testid="collapsible-step-toggle"
            className={classNames(
              "flex h-6 w-6 cursor-pointer select-none items-center justify-center rounded-sm hover:bg-purple-100",
              {
                "opacity-45 hover:opacity-100":
                  step.state === State.WaitingForDependencies && !active,
              },
            )}
          >
            <Icon
              icon="heroicons/outline/chevron-right"
              className={classNames("h-3 w-3 transition-transform duration-150", {
                "rotate-90": isExpanded,
              })}
            />
          </button>
        ) : undefined
      }
    />
  );
}
